import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * Standard display for a single user/group option in a dropdown.
 *
 * Use it from an ejs-dropdownlist item/value template so every user picker in the
 * app reads the same way:
 *
 *   <ng-template #userItem let-data>
 *     <app-user-option [option]="data"></app-user-option>
 *   </ng-template>
 *   <ng-template #userValue let-data>
 *     <app-user-option [option]="data" [compact]="true"></app-user-option>
 *   </ng-template>
 *
 * What gets shown is deliberately different for the two kinds of option, because
 * the underlying records genuinely carry different information:
 *
 *   Group - name only, plus a "Group" badge. Groups have no email address; the
 *           users-dropdown query admits them precisely BECAUSE they are groups
 *           (`includeGroups && u.IsGroup || u.EmailAddress != ''`), so an email
 *           line for a group would always be blank.
 *
 *   User  - first + last name, then the email address, which is how people identify
 *           each other here and is guaranteed non-empty by that same query.
 *           `userName` is a fallback for callers using a looser query.
 *           A user without portal access is called out: granting tank permissions
 *           to someone who cannot sign in is usually a mistake worth seeing.
 */
@Component({
  selector: 'app-user-option',
  templateUrl: './user-option.component.html',
  styleUrls: ['./user-option.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserOptionComponent {
  /** A UserDropdownDTO, or the placeholder entry a dropdown puts at index 0. */
  @Input() option: any;

  /** Single line, for the collapsed value display rather than the popup list. */
  @Input() compact = false;

  /**
   * Off by default. The rule below still evaluates - flipping this input on is all
   * that is needed to surface it once we decide the callout earns its space.
   */
  @Input() showPortalAccessWarning = false;

  /** The "User / Group" style placeholder row carries only display text. */
  get isPlaceholder(): boolean {
    return !this.option?.name;
  }

  get isGroup(): boolean {
    return this.option?.isGroup === true;
  }

  /**
   * Groups are named by `name` (their abbreviated name IS the group name).
   *
   * Users get first + last. `name` arrives from the API as AbbreviatedName when the
   * record has one - initialisms like "A. Cajes" - which is too lossy to pick from a
   * list of colleagues. `name` stays as the fallback for records with no first/last.
   */
  get primaryText(): string {
    if (this.isPlaceholder) return this.option?.text ?? '';
    if (this.isGroup) return this.option?.name ?? '';

    const fullName = [this.option?.firstName, this.option?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return fullName || this.option?.name || '';
  }

  /** Groups have no email; users are identified by theirs. */
  get secondaryText(): string {
    if (this.isGroup) return '';
    return this.option?.emailAddress || this.option?.userName || '';
  }

  /** The rule itself: a user who cannot sign in to the portal. */
  get lacksPortalAccess(): boolean {
    return !this.isGroup && this.option?.hasWebAccess === false;
  }

  get showWarning(): boolean {
    return this.showPortalAccessWarning && this.lacksPortalAccess;
  }
}
